"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameplayScene = void 0;
const tslib_1 = require("tslib");
const phaser_1 = tslib_1.__importDefault(require("phaser"));
const AvatarState_1 = require("../models/AvatarState");
const DebugLayer_1 = require("../debug/DebugLayer");
const GameplayUtils_1 = require("./GameplayUtils");
const GameplayBackground_1 = require("./GameplayBackground");
const GameplayAvatar_1 = require("./GameplayAvatar");
const GameplayTextBubble_1 = require("./GameplayTextBubble");
const GameplayClickArea_1 = require("./GameplayClickArea");
const GameplayRenderer_1 = require("../GameplayRenderer");
const GameplayDropoffAnalytics_1 = require("./GameplayDropoffAnalytics");
const consts_1 = require("../consts");
class GameplayScene extends phaser_1.default.Scene {
    constructor() {
        super({ key: 'GameplayScene' });
        this._debugConfig = {};
        this.leftAvatarState = AvatarState_1.AvatarState.Hidden;
        this.rightAvatarState = AvatarState_1.AvatarState.Hidden;
        this.avatarsLoadingNum = 0;
        this.avatarsLoadingState = [];
    }
    init(data) {
        this._avatarResolver = data.avatarResolver;
        this._getDevicePixelRatio = data.getDevicePixelRatio;
        this._debugConfig = data.debug || {};
        this._renderer = data.renderer;
        this.avatarsLoadingState = [];
        this.showLeftAvatar = this.showLeftAvatar.bind(this);
        this.showRightAvatar = this.showRightAvatar.bind(this);
        this.updateLeftAvatar = this.updateLeftAvatar.bind(this);
        this.updateRightAvatar = this.updateRightAvatar.bind(this);
        // image debug stuff
        // this.textures.on(Phaser.Textures.Events.ADD, (key: string, texture: Texture) => {
        //     console.debug('IMAGE LOADED', key, texture);
        // });
        // this.textures.on(Phaser.Textures.Events.ERROR, (key: string) => {
        //     console.debug('IMAGE FAILED', key);
        // });
    }
    create() {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            this.createLetterboxViewport();
        }
        this.createBackground();
        this.createAvatars();
        this.createClickArea();
        this.createTextBubble();
        this.createDebugLayer();
        this.createDropoffAnalytics();
    }
    createLetterboxViewport() {
        const { width: cameraWidth, height: cameraHeight } = this.cameras.main;
        const { viewportSize, viewportBgColor } = GameplayRenderer_1.GameplayRenderer.letterboxViewportSettings;
        const { width: viewportWidth, height: viewportHeight } = viewportSize;
        const bgColor = viewportBgColor !== null && viewportBgColor !== void 0 ? viewportBgColor : 0x333333;
        this.letterboxViewport = new phaser_1.default.GameObjects.Container(this);
        this.add.existing(this.letterboxViewport);
        const scaleW = cameraWidth / viewportWidth;
        const scaleH = cameraHeight / viewportHeight;
        const viewportScale = Math.min(scaleW, scaleH);
        const viewportWidthScaled = viewportScale * viewportWidth;
        const viewportHeightScaled = viewportScale * viewportHeight;
        this.letterboxViewport.scale = viewportScale;
        this.letterboxViewport.x = cameraWidth / 2 - viewportWidthScaled / 2;
        this.letterboxViewport.y = cameraHeight / 2 - viewportHeightScaled / 2;
        const viewportPreview = new phaser_1.default.GameObjects.Graphics(this);
        viewportPreview.fillStyle(bgColor);
        viewportPreview.fillRect(0, 0, viewportWidth, viewportHeight);
        this.letterboxViewport.add(viewportPreview);
        const letterboxMaskGraphics = new phaser_1.default.GameObjects.Graphics(this);
        letterboxMaskGraphics.fillStyle(0x000000);
        letterboxMaskGraphics.fillRect(this.letterboxViewport.x, this.letterboxViewport.y, viewportWidthScaled, viewportHeightScaled);
        const letterboxGeometryMask = letterboxMaskGraphics.createGeometryMask();
        this.letterboxViewport.mask = letterboxGeometryMask;
    }
    createBackground() {
        var _a;
        const { width, height } = this.cameras.main;
        this.bg = new GameplayBackground_1.GameplayBackground(this);
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            (_a = this.letterboxViewport) === null || _a === void 0 ? void 0 : _a.add(this.bg);
        }
        else {
            this.add.existing(this.bg);
        }
        this.bgCurtain = this.add.graphics({
            x: 0,
            y: 0,
            fillStyle: {
                color: 0x000000,
                alpha: 0.66
            }
        });
        this.bgCurtain.fillRect(0, 0, width, height);
        this.add.existing(this.bgCurtain);
        this.bgCurtain.setAlpha(0);
    }
    setStandardAvatarScale(avatar) {
        const scaleY = this.avatarScale;
        const scaleX = this.avatarScale * avatar.getScaleDir();
        avatar.setScale(scaleX, scaleY);
    }
    setLetterBoxAvatarScale(avatar) {
        const { viewportSize } = GameplayRenderer_1.GameplayRenderer.letterboxViewportSettings;
        const { width: viewportWidth, height: viewportHeight } = viewportSize;
        const { calculatedWidth, calculatedHeight } = avatar;
        const scaleX = viewportWidth / calculatedWidth;
        const scaleY = viewportHeight / calculatedHeight;
        const avatarScale = Math.min(scaleX, scaleY);
        avatar.setScale(avatarScale * avatar.getScaleDir(), avatarScale);
    }
    get avatarScale() {
        const { height } = this.cameras.main;
        return height * 0.9 / GameplayAvatar_1.AVATAR_BUILD_PARAMS.AVATAR_H;
    }
    setAvatarScale(avatar) {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            this.setLetterBoxAvatarScale(avatar);
        }
        else {
            this.setStandardAvatarScale(avatar);
        }
    }
    get avatarYPosition() {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            return GameplayRenderer_1.GameplayRenderer.letterboxViewportSettings.viewportSize.height;
        }
        return this.cameras.main.height;
    }
    getAvatarXPositionForLetterbox() {
        const { viewportSize } = GameplayRenderer_1.GameplayRenderer.letterboxViewportSettings;
        const { width: viewportWidth } = viewportSize;
        const x = viewportWidth / 2;
        // const x = this.letterboxViewport!.scale * viewportWidth;
        return x;
    }
    get leftAvatarVisibleXPosition() {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            return this.getAvatarXPositionForLetterbox();
        }
        const { width } = this.cameras.main;
        return Math.round(-width * 0.15 + width / 2);
    }
    get leftAvatarHiddenXPosition() {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            return this.getAvatarXPositionForLetterbox();
        }
        const { width } = this.cameras.main;
        return Math.round(this.leftAvatarVisibleXPosition - width);
    }
    get rightAvatarVisibleXPosition() {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            return this.getAvatarXPositionForLetterbox();
        }
        const { width } = this.cameras.main;
        return Math.round(width * 0.15 + width / 2);
    }
    get rightAvatarHiddenXPosition() {
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            return this.getAvatarXPositionForLetterbox();
        }
        const { width } = this.cameras.main;
        return Math.round(this.rightAvatarVisibleXPosition + width);
    }
    get isLeftAvatarVisible() {
        return this.leftAvatar.x > this.leftAvatarHiddenXPosition + 0.1;
    }
    get isRightAvatarVisible() {
        return this.rightAvatar.x < this.rightAvatarHiddenXPosition - 0.1;
    }
    get avatarAnimationEasing() {
        return GameplayUtils_1.CSS_EASING['ease-out'];
    }
    createAvatars() {
        var _a, _b;
        // const scale = 1;//this.avatarScale;
        const yPosition = this.avatarYPosition;
        this.leftAvatar = new GameplayAvatar_1.GameplayAvatar(this, consts_1.SIDE.LEFT);
        this.rightAvatar = new GameplayAvatar_1.GameplayAvatar(this, consts_1.SIDE.RIGHT);
        if (GameplayRenderer_1.GameplayRenderer.isLetterBox()) {
            (_a = this.letterboxViewport) === null || _a === void 0 ? void 0 : _a.add(this.leftAvatar);
            (_b = this.letterboxViewport) === null || _b === void 0 ? void 0 : _b.add(this.rightAvatar);
        }
        else {
            this.add.existing(this.leftAvatar);
            this.add.existing(this.rightAvatar);
        }
        this.leftAvatar.setPosition(this.leftAvatarHiddenXPosition, yPosition);
        this.rightAvatar.setPosition(this.rightAvatarHiddenXPosition, yPosition);
        // this.leftAvatar.setScale(scale);
        // this.rightAvatar.setScale(-scale, scale);
    }
    updateLeftAvatar(avatarConfig, immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.updateAvatar(this.showLeftAvatar, this.leftAvatar, this.leftAvatarState, avatarConfig, immediate);
        });
    }
    updateRightAvatar(avatarConfig, immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.updateAvatar(this.showRightAvatar, this.rightAvatar, this.rightAvatarState, avatarConfig, immediate);
        });
    }
    updateAvatar(showAvatarFunction, avatar, avatarState, avatarConfig, immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const avatarNum = ++this.avatarsLoadingNum;
            this.avatarsLoadingState.push({
                loadingNum: avatarNum,
                isLoaded: false,
            });
            avatar === null || avatar === void 0 ? void 0 : avatar.setAvatarConfigAndResolver(avatarConfig, this._avatarResolver);
            const loadedObj = yield (avatar === null || avatar === void 0 ? void 0 : avatar.loadAllAvatarImages(avatarNum));
            if ((loadedObj === null || loadedObj === void 0 ? void 0 : loadedObj.loadingNum) !== this.avatarsLoadingNum) {
                return false;
            }
            // this.rightAvatar!.draw(avatarConfig, this._avatarResolver!, this.avatarsLoadingNum);
            avatar === null || avatar === void 0 ? void 0 : avatar.drawAvatarOnTexture();
            this.setAvatarScale(avatar);
            if (!this._renderer) {
                return false;
            }
            const canShowImmediate = [AvatarState_1.AvatarState.Shown, AvatarState_1.AvatarState.Showing].includes(avatarState);
            const showImmediate = immediate || canShowImmediate;
            yield showAvatarFunction(showImmediate);
            return true;
        });
    }
    showAvatar(immediate = false, avatarVariables) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (avatarVariables.avatarTween) {
                avatarVariables.avatarState = AvatarState_1.AvatarState.Shown;
                avatarVariables.avatarTween.stop();
                avatarVariables.avatarTween = undefined;
            }
            if (immediate) {
                avatarVariables.avatarState = AvatarState_1.AvatarState.Shown;
                avatarVariables.avatar.setPosition(avatarVariables.avatarVisibleXPosition, avatarVariables.avatarYPosition);
                avatarVariables.avatar.setAlpha(1);
            }
            else {
                avatarVariables.avatarState = AvatarState_1.AvatarState.Showing;
                avatarVariables.avatar.setPosition(avatarVariables.avatarHiddenXPosition, avatarVariables.avatarYPosition);
                avatarVariables.avatar.setAlpha(0);
                yield new Promise(resolve => {
                    avatarVariables.avatarTween = this.tweens.add({
                        targets: avatarVariables.avatar,
                        duration: 750,
                        ease: this.avatarAnimationEasing,
                        props: {
                            x: avatarVariables.avatarVisibleXPosition,
                            alpha: 1,
                        },
                        onComplete: () => {
                            avatarVariables.avatarState = AvatarState_1.AvatarState.Shown;
                            resolve();
                        },
                    });
                });
            }
        });
    }
    showLeftAvatar(immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.showAvatar(immediate, {
                avatarTween: this.leftAvatarTween,
                avatarState: this.leftAvatarState,
                avatar: this.leftAvatar,
                avatarVisibleXPosition: this.leftAvatarVisibleXPosition,
                avatarHiddenXPosition: this.leftAvatarHiddenXPosition,
                avatarYPosition: this.avatarYPosition,
            });
        });
    }
    showRightAvatar(immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.showAvatar(immediate, {
                avatarTween: this.rightAvatarTween,
                avatarState: this.rightAvatarState,
                avatar: this.rightAvatar,
                avatarVisibleXPosition: this.rightAvatarVisibleXPosition,
                avatarHiddenXPosition: this.rightAvatarHiddenXPosition,
                avatarYPosition: this.avatarYPosition,
            });
        });
    }
    hideAvatar(immediate = false, avatarVariables) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (avatarVariables.avatarTween) {
                avatarVariables.avatarTween.stop();
                avatarVariables.avatarTween = undefined;
            }
            if (immediate) {
                avatarVariables.avatarState = AvatarState_1.AvatarState.Hidden;
                avatarVariables.avatar.setPosition(avatarVariables.avatarHiddenXPosition, avatarVariables.avatarYPosition);
                avatarVariables.avatar.setAlpha(0);
            }
            else {
                avatarVariables.avatarState = AvatarState_1.AvatarState.Hiding;
                avatarVariables.avatar.setPosition(avatarVariables.avatarVisibleXPosition, avatarVariables.avatarYPosition);
                avatarVariables.avatar.setAlpha(1);
                yield new Promise(resolve => {
                    avatarVariables.avatarTween = this.tweens.add({
                        targets: avatarVariables.avatar,
                        duration: 750,
                        ease: this.avatarAnimationEasing,
                        props: {
                            x: this.leftAvatarHiddenXPosition,
                            alpha: 0,
                        },
                        onComplete: () => {
                            avatarVariables.avatarState = AvatarState_1.AvatarState.Hidden;
                            resolve();
                        },
                    });
                });
            }
        });
    }
    hideLeftAvatar(immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.hideAvatar(immediate, {
                avatarTween: this.leftAvatarTween,
                avatarState: this.leftAvatarState,
                avatar: this.leftAvatar,
                avatarVisibleXPosition: this.leftAvatarVisibleXPosition,
                avatarHiddenXPosition: this.leftAvatarHiddenXPosition,
                avatarYPosition: this.avatarYPosition,
            });
        });
    }
    hideRightAvatar(immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.hideAvatar(immediate, {
                avatarTween: this.rightAvatarTween,
                avatarState: this.rightAvatarState,
                avatar: this.rightAvatar,
                avatarVisibleXPosition: this.rightAvatarVisibleXPosition,
                avatarHiddenXPosition: this.rightAvatarHiddenXPosition,
                avatarYPosition: this.avatarYPosition,
            });
        });
    }
    set leftAvatarEnabled(value) {
        this.leftAvatar.setVisible(value);
    }
    set rightAvatarEnabled(value) {
        this.rightAvatar.setVisible(value);
    }
    /**
     * @deprecated
     */
    animateStoryEndingRightAvatar() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const scale = this.rightAvatar.scale; // this.avatarScale;
            this.rightAvatar.setScale(-scale, scale);
            this.rightAvatar.setPosition(this.rightAvatarHiddenXPosition, this.avatarYPosition);
            this.rightAvatar.setAlpha(1);
            yield new Promise(resolve => {
                this.tweens.add({
                    targets: this.rightAvatar,
                    duration: 750,
                    ease: this.avatarAnimationEasing,
                    props: {
                        x: this.cameras.main.width / 2,
                        scaleX: -scale * 0.9,
                        scaleY: scale * 0.9,
                    },
                    onComplete: resolve,
                });
            });
        });
    }
    showCurtainOverBackground() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.bgCurtain.setAlpha(0);
            yield new Promise(resolve => {
                this.tweens.add({
                    targets: this.bgCurtain,
                    duration: 1000,
                    ease: GameplayUtils_1.CSS_EASING['ease-out'],
                    props: {
                        alpha: 1,
                    },
                    onComplete: resolve,
                });
            });
        });
    }
    getGameplayRenderer() {
        return this._renderer;
    }
    createTextBubble() {
        this.textBubble = new GameplayTextBubble_1.GameplayTextBubble(this, this._getDevicePixelRatio, this._renderer.stylesAlias, this._renderer.isMultiplayer);
        this.add.existing(this.textBubble);
    }
    createDropoffAnalytics() {
        this.dropoffAnalyticsView = new GameplayDropoffAnalytics_1.GameplayDropoffAnalytics(this, this._renderer.stylesAlias);
    }
    createClickArea() {
        this.clickArea = new GameplayClickArea_1.GameplayClickArea(this);
        this.add.existing(this.clickArea);
    }
    createDebugLayer() {
        if (!this._debugConfig) {
            return;
        }
        if (!(this._debugConfig.showFps ||
            this._debugConfig.showDPR ||
            this._debugConfig.showResolution ||
            this._debugConfig.showTextureCacheSize)) {
            return;
        }
        this.debugLayer = new DebugLayer_1.DebugLayer(this, 50, 50, this._getDevicePixelRatio, this._debugConfig);
        const rect = this.debugLayer.getBounds();
        this.add.existing(this.debugLayer);
        this.debugLayer.setPosition(20, this.cameras.main.height - rect.height - 40);
    }
    getTexturesCacheSize() {
        return Object.entries(this.textures.list)
            .filter(([key]) => key.indexOf('__') !== 0)
            .map(([_key, texture]) => texture.source[0].width * texture.source[0].height * 4)
            .reduce((acc, size) => acc + size, 0);
    }
    releaseTextureByKey(key) {
        if (!GameplayRenderer_1.GameplayRenderer.aggressiveMemoryManagement) {
            return;
        }
        const texture = this.textures.get(key);
        if (!texture || texture.key === '__MISSING' || texture.key === '__DEFAULT') {
            return;
        }
        const url = GameplayRenderer_1.GameplayRenderer.getUrlByImageKey(key);
        if (!url || !GameplayRenderer_1.GameplayRenderer.onAssetUnloadHandler(url)) {
            return;
        }
        // console.debug(`releasing asset with url = ${url}`);
        // console.debug(`release texture by key ${key} with url ${url}`);
        this.load.list.entries = this.load.list.entries
            .filter(file => !(file instanceof phaser_1.default.Loader.FileTypes.ImageFile && file.url.indexOf(url) === 0));
        this.load.inflight.entries = this.load.inflight.entries
            .filter(file => {
            if (file instanceof phaser_1.default.Loader.FileTypes.ImageFile && file.url.indexOf(url) === 0) {
                if (file.xhrLoader) {
                    file.xhrLoader.abort();
                }
                return true;
            }
            return false;
        });
        // remove texture from cache
        // console.debug(`releasing texture with key = ${key}`);
        this.textures.remove(key);
    }
    update(time, delta) {
        var _a, _b, _c;
        super.update(time, delta);
        (_a = this.debugLayer) === null || _a === void 0 ? void 0 : _a.update(time, delta);
        (_b = this.leftAvatar) === null || _b === void 0 ? void 0 : _b.update(time, delta);
        (_c = this.rightAvatar) === null || _c === void 0 ? void 0 : _c.update(time, delta);
    }
    preventAvatarLoadingRaceCondition() {
        this.avatarsLoadingNum++;
    }
}
exports.GameplayScene = GameplayScene;
